import CloseFilledIcon from '@/assets/close-filled.svg?react'
import LinkIcon from '@/assets/link.svg?react'
import ClickWrapper, { type ClickWrapperProps } from '@/components/ClickWrapper'
import Tooltip from '@/components/Tooltip'
import { READABILITY_EXTRACT } from '@/constants/api'
import type { LinkInfo } from '@/utils/fetchers/attachment'
import { post } from '@/utils/request'
import { Button, Input, rem } from '@mantine/core'
import clsx from 'clsx'
import { isEmpty } from 'lodash-es'
import { useCallback, useState } from 'react'
import styles from './LinkAttachmentPicker.module.css'

export interface LinkAttachmentPickerProps extends Omit<ClickWrapperProps, 'onSelect'> {
	onSelect?: (link: LinkInfo) => void
	disabled?: boolean
}

export default function LinkAttachmentPicker({
	disabled = false,
	onSelect,
	className,
	...props
}: LinkAttachmentPickerProps) {
	const [content, setContent] = useState<string>('')
	const [isShow, setIsShow] = useState<boolean>(false)

	const [isLoading, setIsLoading] = useState<boolean>(false)
	const parseLinkInfo = useCallback(() => {
		if (isEmpty(content)) {
			return
		}
		setIsLoading(true)
		post<{ data: LinkInfo }>(READABILITY_EXTRACT, {
			link: content,
		})
			.then((res) => res.data.data)
			.then((data) => void onSelect?.(data))
			.then(() => {
				setContent('')
				setIsShow(false)
			})
			.finally(() => {
				setIsLoading(false)
			})
	}, [content, onSelect])

	return (
		<Tooltip
			opened={isShow}
			onClose={() => setIsShow(false)}
			withArrow
			offset={10}
			position="bottom"
			styles={{
				dropdown: {
					backgroundColor: 'var(--bg-on-body-1)',
					padding: '20px',
					boxShadow: '0 10px 40px var(--solid-shadow-1)',
					borderRadius: '8px',
					width: '400px',
				},
				arrow: {
					backgroundColor: 'var(--bg-on-body-1)',
				},
			}}
			content={
				<div className={styles.container}>
					<header className={styles.header}>
						<h1 className={styles.title}>添加链接</h1>
						<Button variant="outline" size="sm" onClick={parseLinkInfo} loading={isLoading}>
							添加
						</Button>
					</header>
					<div className={styles.description}>
						可以添加微信、微博、知乎、秒拍、爱奇艺等各类网站链接，支持主流视频网站嵌入播放
					</div>
					<Input
						value={content}
						onChange={(e) => setContent(e.target.value)}
						variant="filled"
						autoFocus
						size="md"
						rightSectionPointerEvents="auto"
						rightSection={
							content !== '' ? (
								<CloseFilledIcon
									role="button"
									aria-label="清除链接"
									title="清除链接"
									onClick={() => setContent('')}
									className={styles.closeIcon}
								/>
							) : undefined
						}
						vars={() => ({
							wrapper: {
								'--input-height': rem('40px'),
								'--input-padding-y': rem('5px'),
								'--input-radius': rem('8px'),
								'--input-bg': 'var(--bg-surface-body-1)',
							},
						})}
					/>
				</div>
			}
		>
			<ClickWrapper
				className={clsx(styles.wrapper, className)}
				title="链接"
				onClick={(e) => {
					e.stopPropagation()
					if (disabled) {
						return
					}
					setIsShow((prev) => !prev)
				}}
				{...props}
			>
				<LinkIcon
					className={clsx(
						styles.linkIcon,
						disabled ? styles.linkIconDisabled : styles.linkIconEnabled
					)}
				/>
			</ClickWrapper>
		</Tooltip>
	)
}
